/**
 * StatsSection.tsx
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import type { PublicStats } from '@/hooks/useStats';

interface StatsSectionProps {
  stats?: PublicStats | null;
}

interface CorpusStats {
  total:                    number;
  arrets_principe:          number;
  rgpd_count:               number;
  cjue_count:               number;
  reglements_eu_count:      number;
  reglements_eu_reglements: number;
  articles_codes_count:     number;
  documents_total:          number;
}

// valueKey / labelKey : clés i18n résolues au rendu (valueKey absent = valeur littérale)
const RELIABILITY_KPIS = [
  { emoji: '🎯', valueKey: 'jurisprudence.stats.kpi_sla_value',   value: '',      labelKey: 'jurisprudence.stats.kpi_sla',      bg: 'bg-green-50 dark:bg-green-950/30' },
  { emoji: '⚡', valueKey: '',                                     value: '< 5 s', labelKey: 'jurisprudence.stats.kpi_response', bg: 'bg-amber-50 dark:bg-amber-950/30' },
  { emoji: '🔒', valueKey: 'jurisprudence.stats.kpi_gdpr_value',  value: '',      labelKey: 'jurisprudence.stats.kpi_hosting',  bg: 'bg-blue-50 dark:bg-blue-950/30' },
  { emoji: '📅', valueKey: 'jurisprudence.stats.kpi_daily_value', value: '',      labelKey: 'jurisprudence.stats.kpi_daily',    bg: 'bg-teal-50 dark:bg-teal-950/30' },
];

export const StatsSection = ({ stats }: StatsSectionProps) => {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const [corpus, setCorpus] = useState<CorpusStats | null>(null);

  useEffect(() => {
    fetch('/api/fiches/corpus-stats')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCorpus(d))
      .catch(() => null);
  }, []);

  // Total agrégé : décisions FR + CJUE + règlements EU + articles de loi
  const totalDocuments = corpus?.documents_total
    ? `${Math.round(corpus.documents_total / 1000)}k`
    : stats
      ? `${Math.round(stats.total / 1000)}k`
      : `${(1600).toLocaleString(locale)}k`;

  const citationsTotal = stats?.citations_total ? `${Math.round(stats.citations_total / 1000)}k` : '142k';
  const domainesCount  = stats?.domaines?.length ?? stats?.domaines_detail?.length ?? 8;

  const MAIN_STATS = [
    { value: totalDocuments, suffix: '+',    label: t('jurisprudence.stats.documents_label'), sub: t('jurisprudence.stats.documents_sub') },
    { value: citationsTotal, suffix: '',     label: t('jurisprudence.stats.links_label'),     sub: t('jurisprudence.stats.links_sub') },
    { value: '75',           suffix: t('jurisprudence.stats.years_suffix'), label: t('jurisprudence.stats.coverage_label'), sub: t('jurisprudence.stats.coverage_sub') },
    { value: String(domainesCount), suffix: '', label: t('jurisprudence.stats.domains_label'), sub: t('jurisprudence.stats.domains_sub') },
  ];

  const CORPUS_ITEMS = [
    {
      to:     '/jurisprudence/principes',
      emoji:  '⭐',
      label:  t('jurisprudence.stats.corpus_principes'),
      count:  corpus?.arrets_principe ?? null,
      unit:   t('jurisprudence.stats.unit_rulings'),
      color:  'text-amber-600 dark:text-amber-400',
      border: 'border-amber-200 dark:border-amber-800',
      bg:     'bg-amber-50/60 dark:bg-amber-900/10',
    },
    {
      to:     '/jurisprudence/rgpd',
      emoji:  '🔒',
      label:  t('jurisprudence.stats.corpus_rgpd'),
      count:  corpus?.rgpd_count ?? null,
      unit:   t('jurisprudence.stats.unit_decisions'),
      color:  'text-blue-600 dark:text-blue-400',
      border: 'border-blue-200 dark:border-blue-800',
      bg:     'bg-blue-50/60 dark:bg-blue-900/10',
    },
    {
      to:     '/jurisprudence/cjue',
      emoji:  '🇪🇺',
      label:  t('jurisprudence.stats.corpus_cjue'),
      count:  corpus?.cjue_count ?? null,
      unit:   t('jurisprudence.stats.unit_rulings'),
      color:  'text-indigo-600 dark:text-indigo-400',
      border: 'border-indigo-200 dark:border-indigo-800',
      bg:     'bg-indigo-50/60 dark:bg-indigo-900/10',
    },
    {
      to:     '/reglements',
      emoji:  '📋',
      label:  t('jurisprudence.stats.corpus_reglements'),
      count:  corpus?.reglements_eu_count ?? null,
      unit:   t('jurisprudence.stats.unit_articles'),
      color:  'text-violet-600 dark:text-violet-400',
      border: 'border-violet-200 dark:border-violet-800',
      bg:     'bg-violet-50/60 dark:bg-violet-900/10',
    },
  ];

  return (
    <section className="py-16 bg-muted/30 border-t border-border">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-8">

        <div className="mb-9">
          <span className="block text-[10px] font-bold uppercase tracking-[.14em] text-primary mb-2.5">
            {t('jurisprudence.stats.eyebrow')}
          </span>
          <h2 className="font-serif text-[30px] sm:text-[34px] font-bold text-foreground tracking-[-0.03em] leading-tight">
            {t('jurisprudence.stats.h2_1')} <br />
            <em className="not-italic text-primary">{t('jurisprudence.stats.h2_2')}</em>
          </h2>
        </div>

        {/* ── 4 stats principales ── */}
        <div
          className="grid grid-cols-2 sm:grid-cols-4 mb-5"
          style={{ borderTop: '1px solid hsl(var(--border))', borderLeft: '1px solid hsl(var(--border))' }}
        >
          {MAIN_STATS.map(({ value, suffix, label, sub }) => (
            <div key={label} className="px-6 py-6"
              style={{ borderRight: '1px solid hsl(var(--border))', borderBottom: '1px solid hsl(var(--border))' }}>
              <p className="font-serif text-[36px] font-bold text-primary tracking-[-0.04em] leading-none mb-1.5">
                {value}
                {suffix && <span className="text-[18px] text-muted-foreground/60 font-normal">{suffix}</span>}
              </p>
              <p className="text-[13px] font-semibold text-foreground mb-0.5">{label}</p>
              <p className="text-[11px] text-muted-foreground">{sub}</p>
            </div>
          ))}
        </div>

        {/* ── Corpus spécialisés ── */}
        <div className="mb-5">
          <p className="text-[10px] font-bold uppercase tracking-[.12em] text-muted-foreground/60 mb-3">
            {t('jurisprudence.stats.corpus_title')}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CORPUS_ITEMS.map(({ to, emoji, label, count, unit, color, border, bg }) => (
              <Link
                key={to}
                to={to}
                className={`rounded-xl border ${border} ${bg} px-4 py-3.5 hover:opacity-80 transition-opacity`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base leading-none">{emoji}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${color}`}>{label}</span>
                </div>
                {count != null && count > 0 ? (
                  <p className="font-serif text-[22px] font-bold text-foreground tracking-tight leading-none">
                    {count.toLocaleString(locale)}
                    <span className="text-[11px] font-normal text-muted-foreground ml-1">{unit}</span>
                  </p>
                ) : (
                  <div className="h-6 w-16 bg-border/40 rounded animate-pulse" />
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* ── KPIs fiabilité ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {RELIABILITY_KPIS.map(({ emoji, valueKey, value, labelKey, bg }) => (
            <div key={labelKey} className="bg-card border border-border rounded-xl px-4 py-3.5 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center text-base flex-shrink-0`}>
                {emoji}
              </div>
              <div>
                <p className="text-[15px] font-bold text-foreground leading-tight">{valueKey ? t(valueKey) : value}</p>
                <p className="text-[11px] text-muted-foreground leading-snug">{t(labelKey)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Link to="/legal-graph"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
            {t('jurisprudence.stats.explore_graph')}
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};
